"use client";

/**
 * A CHEGADA numa lista viva (laboratório: itens 14 listas e 22 criar e
 * apagar). Quem acabou de aparecer entra na vez dele e acende uma vez; quem
 * já estava na primeira pintura aparece pronto.
 *
 * - `useOrdemDeEntrada`: a vez de cada chave que acabou de aparecer, o
 *   índice da cascata.
 * - `useChegouAgora`: se a chave nasceu DEPOIS que a lista montou.
 * - `LinhaViva`: a linha que entra subindo e acende (o véu `destaque`),
 *   acende de novo quando a quantidade sobe e sai encolhendo fora do fluxo.
 *
 * Nasceram no kit da tabela do web e no terminal do PDV; o kiosk tinha as
 * mesmas cópias. O CSS do véu (`.mov-linha-veu`: absoluto, por cima da
 * linha, sem pegar toque) mora em cada app, e a linha precisa ser
 * posicionada pra segurá-lo.
 */
import { forwardRef, useCallback, useEffect, useRef, useState, type ReactNode, type Ref } from "react";
import { m, type HTMLMotionProps } from "motion/react";
import { useDestaque } from "./gestos";
import { useMovimento } from "./provider";

/**
 * Ordem de ENTRADA de cada chave que acabou de aparecer (0 = a primeira).
 * É o índice da cascata: quem já estava na lista não entra de novo, então só
 * os novos esperam a vez (apagar uma linha não faz a que sobe da página
 * seguinte esperar a vez da 8ª). O teto da cascata é da receita (`item`).
 */
export function useOrdemDeEntrada(chaves: readonly string[]): Map<string, number> {
  const anteriores = useRef<Set<string>>(new Set());
  const ordem = new Map<string, number>();
  let k = 0;
  for (const c of chaves) if (!anteriores.current.has(c)) ordem.set(c, k++);
  useEffect(() => {
    anteriores.current = new Set(chaves);
  });
  return ordem;
}

/**
 * Diz se a chave nasceu DEPOIS que a lista montou. O que estava lá na primeira
 * pintura (o carrinho restaurado depois de um F5, o pedido que veio da fila)
 * não foi criado agora, então não acende.
 *
 * `pronto`: a lista que CARREGA depois de abrir (a folha da contagem) só conta
 * como montada quando o dado chega; até lá nada é chegada.
 */
export function useChegouAgora(chaves: readonly string[], pronto = true): (chave: string) => boolean {
  const naMontagem = useRef<Set<string> | null>(null);
  if (pronto && naMontagem.current === null) naMontagem.current = new Set(chaves);
  /* A linha da montagem que SAIU e voltou já é chegada: some do conjunto
     assim que deixa a lista. */
  useEffect(() => {
    const inicio = naMontagem.current;
    if (!inicio) return;
    const vivas = new Set(chaves);
    for (const c of inicio) if (!vivas.has(c)) inicio.delete(c);
  });
  return (chave) => naMontagem.current !== null && !naMontagem.current.has(chave);
}

type PropsDaLinhaViva = Omit<HTMLMotionProps<"div">, "children"> & {
  /** A tag da linha: `li` quando a lista é um `ul` (o carrinho do kiosk). */
  como?: "div" | "li";
  /** A vez na cascata (0 = a primeira; a receita `item` para no 10º). */
  ordem?: number;
  /** `false`: a linha aparece pronta (já estava lá), mas ainda sai e muda de lugar. */
  entra?: boolean;
  /**
   * `false`: a linha fora da página que se vê. Aparece e some seca e não se
   * mede (o teto de custo do `layout` é a página de 50).
   */
  anda?: boolean;
  /** A linha chegou agora (criada aqui, ou trazida pelo tempo real): acende na chegada. */
  chegou?: boolean;
  /** A quantidade da linha: quando ela SOBE (o mesmo item de novo), a linha acende. */
  qtd?: number;
  /**
   * Onde acende. `veu` (padrão): uma camada por cima da linha, que não briga
   * com o fundo dela. `fundo`: o próprio fundo da linha, pra linha
   * transparente em grade (`nth-child`), onde a camada a mais desalinharia as
   * colunas; no fim o fundo sai do inline.
   */
  acende?: "veu" | "fundo";
  /**
   * Quando a linha se mede pra andar: só quando a lista muda de forma
   * (entrou, saiu, a linha cresceu). Digitar numa busca re-renderiza a tela
   * inteira e não pode medir a lista a cada tecla.
   */
  dependencia?: string;
  children: ReactNode;
};

/**
 * A linha que ANDA (carrinho do PDV, fila do balcão, listas da Loja, itens da
 * OS): entra subindo 8px na vez dela (receita `item`) e acende uma vez quando
 * chegou agora; quando `qtd` sobe, acende de novo; sai encolhendo 2% fora do
 * fluxo e as vizinhas andam pela mola `layout` (só posição: a linha que
 * cresce não estica o texto das outras). O resto das props vai pro `m.div`
 * (ou `m.li`) e vence as da receita. `forwardRef` porque o `popLayout` mede
 * quem está saindo.
 */
export const LinhaViva = forwardRef<HTMLElement, PropsDaLinhaViva>(function LinhaViva(
  {
    como = "div",
    ordem = 0,
    entra = true,
    anda = true,
    chegou = false,
    qtd = 0,
    acende = "veu",
    dependencia,
    children,
    ...resto
  },
  ref,
) {
  const mov = useMovimento();
  const r = mov.r.item(ordem);
  const [pulso, setPulso] = useState(chegou ? 1 : 0);
  const anterior = useRef(qtd);
  useEffect(() => {
    if (qtd > anterior.current) setPulso((p) => p + 1);
    anterior.current = qtd;
  }, [qtd]);
  /* No modo `fundo` quem acende é a própria linha (o escopo do destaque). */
  const escopo = useDestaque<HTMLElement>(acende === "fundo" ? pulso : 0, { naChegada: acende === "fundo" && chegou });
  const juntar = useCallback(
    (el: HTMLElement | null) => {
      if (acende === "fundo") (escopo as { current: HTMLElement | null }).current = el;
      if (typeof ref === "function") ref(el);
      else if (ref) (ref as { current: HTMLElement | null }).current = el;
    },
    [acende, escopo, ref],
  );
  /* `m.li` e `m.div` aceitam as mesmas props de movimento; o tipo é o do div. */
  const Linha = (como === "li" ? m.li : m.div) as typeof m.div;
  return (
    <Linha
      ref={juntar as Ref<HTMLDivElement>}
      layout={mov.layout ? "position" : false}
      layoutDependency={anda ? dependencia : "parada"}
      transition={{ layout: mov.mola("layout") }}
      initial={anda && entra ? r.initial : false}
      animate={r.animate}
      exit={anda ? r.exit : undefined}
      {...resto}
    >
      {/* O véu mora numa camada própria, por cima da linha, e remonta a cada
          pulso: é ele que acende e apaga, sem brigar com o fundo da linha. */}
      {acende === "veu" && pulso > 0 ? (
        <m.span
          key={pulso}
          className="mov-linha-veu"
          aria-hidden
          initial={{ backgroundColor: "var(--vos-destaque)" }}
          animate={mov.r.destaque}
        />
      ) : null}
      {children}
    </Linha>
  );
});
